import React, { useState, useEffect } from 'react';
import { Radar, TrendingUp, TrendingDown, Newspaper, Compass, AlertTriangle, ShieldCheck, Clock } from 'lucide-react';
import { marketService } from '../../services/api';

export default function SentimentRadar() {
  const [radarData, setRadarData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    marketService.getRadar()
      .then(res => {
        setRadarData(res);
      })
      .catch(err => {
        console.error('Failed to load radar data:', err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const fearGreed = radarData?.fearGreedIndex || { score: 72, label: 'Greed', previousClose: 68 };
  const news = radarData?.newsDigest || [];
  const summary = radarData?.sentimentSummary || 'Tâm lý thị trường ổn định, dòng tiền tích cực.';

  const getGaugeColor = (score) => {
    if (score >= 75) return 'text-emerald-400';
    if (score >= 55) return 'text-cyan-400';
    if (score >= 45) return 'text-amber-400';
    return 'text-rose-400';
  };

  return (
    <div className="space-y-6 animate-fade-in">
      
      {/* 1. Header Toolbar */}
      <div className="glass-panel p-4 sm:p-5 rounded-2xl border border-emerald-500/30 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-400/40 flex items-center justify-center text-emerald-400 shadow-[0_0_15px_rgba(0,255,136,0.25)]">
            <Radar className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-display font-bold text-base text-white uppercase tracking-wider">
              Sentiment & Macro Market Radar
            </h3>
            <p className="text-xs font-mono text-slate-400">
              Đo lường tâm lý đám đông & Tin tức vĩ mô phân tích bằng AI
            </p>
          </div>
        </div>

        <span className="px-3 py-1 rounded-full text-xs font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
          MACRO FEED: LIVE
        </span>
      </div>

      {/* 2. Main Radar Layout: 2 Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Left Col: Fear & Greed Index Gauge (5 cols) */}
        <div className="lg:col-span-5 glass-card p-6 rounded-2xl border border-white/10 flex flex-col justify-between space-y-6">
          <div>
            <div className="flex items-center justify-between pb-3 border-b border-white/10">
              <span className="text-xs font-mono text-slate-400 uppercase tracking-wider">FEAR & GREED INDEX</span>
              <Compass className="w-4 h-4 text-emerald-400" />
            </div>

            {/* Gauge Display */}
            <div className="flex flex-col items-center justify-center py-8">
              <div className="relative w-44 h-44 rounded-full border-4 border-slate-800 flex items-center justify-center shadow-[inset_0_0_30px_rgba(0,0,0,0.8)]">
                {/* Neon Ring */}
                <div 
                  className="absolute inset-0 rounded-full border-4 border-emerald-400/60 transition-all duration-1000"
                  style={{
                    clipPath: `polygon(50% 50%, -50% -50%, ${fearGreed.score}% -50%, 100% 100%, 0% 100%)`
                  }}
                />
                
                <div className="text-center z-10">
                  <div className={`text-5xl font-mono font-bold ${getGaugeColor(fearGreed.score)} tracking-tight`}>
                    {fearGreed.score}
                  </div>
                  <div className="text-sm font-mono font-bold text-white uppercase mt-1 tracking-wider">
                    {fearGreed.label}
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Breakdown */}
            <div className="grid grid-cols-2 gap-2 text-xs font-mono pt-3 border-t border-white/10">
              <div className="p-2.5 rounded-xl bg-slate-950/40 border border-white/5">
                <span className="text-[10px] text-slate-500 block">Hôm qua</span>
                <span className="font-bold text-slate-300">{fearGreed.previousClose} (Tham lam)</span>
              </div>
              <div className="p-2.5 rounded-xl bg-slate-950/40 border border-white/5">
                <span className="text-[10px] text-slate-500 block">Tuần trước</span>
                <span className="font-bold text-slate-300">{fearGreed.oneWeekAgo || 61} (Trung lập)</span>
              </div>
            </div>
          </div>

          {/* AI Sentiment Summary Box */}
          <div className="p-4 rounded-xl bg-emerald-950/20 border border-emerald-500/30 text-xs font-mono leading-relaxed text-emerald-200">
            <div className="font-bold text-emerald-400 mb-1 flex items-center gap-1.5 uppercase text-[11px]">
              <TrendingUp className="w-3.5 h-3.5" />
              <span>AI Nhận Định Dòng Tiền:</span>
            </div>
            {summary}
          </div>
        </div>

        {/* Right Col: Macro News Digest (7 cols) */}
        <div className="lg:col-span-7 glass-card p-6 rounded-2xl border border-white/10 flex flex-col justify-between space-y-4">
          <div>
            <div className="flex items-center justify-between pb-3 border-b border-white/10">
              <span className="text-xs font-mono text-slate-400 uppercase tracking-wider flex items-center gap-2">
                <Newspaper className="w-4 h-4 text-cyan-400" />
                <span>Bản Tin Tác Động Thị Trường (AI Lọc Nhiễu)</span>
              </span>
              <span className="text-[10px] font-mono text-slate-500">REAL-TIME NLP</span>
            </div>

            <div className="mt-4 space-y-3">
              {news.map((n) => {
                const isBull = n.sentiment === 'BULLISH';
                const isBear = n.sentiment === 'BEARISH';

                return (
                  <div 
                    key={n.id}
                    className="p-3.5 rounded-xl bg-slate-950/50 border border-white/5 hover:border-white/20 transition-all space-y-2"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold ${
                          isBull ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40' :
                          isBear ? 'bg-rose-500/20 text-rose-400 border border-rose-500/40' :
                          'bg-slate-800 text-slate-300'
                        }`}>
                          {n.sentiment}
                        </span>

                        <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold ${
                          n.impact === 'HIGH' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-slate-800 text-slate-400'
                        }`}>
                          IMPACT: {n.impact}
                        </span>
                      </div>

                      <span className="text-[10px] font-mono text-slate-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>{n.time}</span>
                      </span>
                    </div>

                    <h5 className="font-sans font-medium text-xs sm:text-sm text-slate-200 leading-snug">
                      {n.title}
                    </h5>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="pt-3 border-t border-white/10 flex items-center justify-between text-[11px] font-mono text-slate-500">
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              <span>Dữ liệu lọc nhiễu tự động qua AI Proxy Gateway</span>
            </span>
            <span>Cập nhật liên tục</span>
          </div>

        </div>

      </div>

    </div>
  );
}
